// Multi-Select Custom Select Component
// Supports both single and multiple selection modes
// For multi-select: stores values as comma-separated strings (e.g., "value1,value2,value3")
// This eliminates the need for array/string conversion when working with databasesimport React, { useState, useRef, useEffect } from "react";
import { useEffect, useRef, useState } from "react";
import { useFormContext, Controller } from "react-hook-form";
import { Label, FormGroup } from "reactstrap";
import styled from "styled-components";

// Styled components for our custom select
const SelectWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const StyledSelect = styled.div<{ isOpen?: boolean; isValid?: boolean; isInvalid?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid ${props =>
		props.isInvalid
			? '#dc3545'
			: props.isValid
				? '#d1d5db'
				: '#d1d5db'};
  border-radius: 0.375rem;
  padding: 0.5rem 0.75rem;
  min-height: 38px;
  background-color: white;
  cursor: pointer;
  box-shadow: ${props =>
		props.isInvalid
			? '0 0 0 0.2rem rgba(220, 53, 69, 0.25)'
			: props.isValid
				? 'none'
				: 'none'};
  
  &:focus, &:hover {
    border-color: ${props =>
		props.isInvalid
			? '#dc3545'
			: props.isValid
				? '#28a745'
				: '#6366f1'};
    outline: none;
  }
`;

const SelectPlaceholder = styled.div`
  color: #6c757d;
`;

const SelectValue = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.25rem;
`;

const SelectedTag = styled.div`
  display: inline-flex;
  align-items: center;
  background-color: #28a745;
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.875rem;
  gap: 0.25rem;
`;

const TagRemoveButton = styled.button`
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 0;
  font-size: 1rem;
  line-height: 1;
  
  &:hover {
    color: #e5e7eb;
  }
`;

const SelectedCount = styled.div`
  color: #374151;
  font-size: 0.875rem;
`;

const DropdownIcon = styled.div<{ isOpen?: boolean }>`
  position: relative;
  width: 12px;
  height: 12px;
  min-width: 12px;
  margin-left: 8px;
  
  &::before, &::after {
    content: '';
    display: block;
    position: absolute;
    width: 8px;
    height: 2px;
    background-color: #6c757d;
    transition: transform 0.2s;
    top: 5px;
  }
  
  &::before {
    transform: ${props => !props.isOpen ? 'rotate(-45deg)' : 'rotate(45deg)'};
    right: 2px;
  }
  
  &::after {
    transform: ${props => !props.isOpen ? 'rotate(45deg)' : 'rotate(-45deg)'};
    right: 7px;
  }
`;

const DropdownMenu = styled.div<{ isOpen?: boolean }>`
  position: absolute;
  top: calc(100% + 5px);
  left: 0;
  right: 0;
  max-height: 200px;
  overflow-y: auto;
  background-color: white;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  z-index: 1000;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  display: ${props => props.isOpen ? 'block' : 'none'};
`;

const OptionItem = styled.div<{ isSelected?: boolean; isHighlighted?: boolean }>`
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: ${props =>
		props.isSelected
			? 'rgba(117, 242, 86, 0.1)'
			: props.isHighlighted
				? 'rgba(173, 245, 155, 0.1)'
				: 'transparent'};
  color: ${props => props.isSelected ? '#28a745' : 'inherit'};
  
  &:hover {
    background-color: ${props => props.isSelected ? 'rgba(117, 242, 86, 0.1)' : 'rgba(173, 245, 155, 0.1)'};
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: none;
  border-bottom: 1px solid #d1d5db;
  outline: none;
  
  &:focus {
    border-color: #6366f1;
  }
`;

// Custom error message
const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 0.75rem;
  margin-top: 0.25rem;
  padding: 0.25rem 0.5rem;
  background-color: rgba(220, 53, 69, 0.05);
  border-radius: 0.25rem;
`;

// SVG icons for validation states
const CheckIcon = () => (
	<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path
			d="M6.00016 10.7799L3.22016 7.9999L2.27016 8.9399L6.00016 12.6699L14.0002 4.6699L13.0602 3.7299L6.00016 10.7799Z"
			fill="#28a745"
		/>
	</svg>
);

const WarningIcon = () => (
	<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path
			d="M8 0C3.584 0 0 3.584 0 8C0 12.416 3.584 16 8 16C12.416 16 16 12.416 16 8C16 3.584 12.416 0 8 0ZM8.8 12H7.2V10.4H8.8V12ZM8.8 8.8H7.2V4H8.8V8.8Z"
			fill="#dc3545"
		/>
	</svg>
);

interface Option {
	value: string | number;
	label: string;
}

interface CustomSelectProps {
	name: string;
	label: string;
	options: Option[];
	required?: boolean;
	placeholder?: string;
	isSearchable?: boolean;
	isClearable?: boolean;
	isMultiple?: boolean;
	maxDisplayTags?: number;
	className?: string;
}

const CustomSelect: React.FC<CustomSelectProps> = ({
	name,
	label,
	options,
	required = false,
	placeholder = "Select...",
	isSearchable = true,
	isClearable = false,
	isMultiple = false,
	maxDisplayTags = 3,
	className,
}) => {
	const {
		control,
		formState: { errors },
		trigger,
		setValue,
		getValues
	} = useFormContext();

	// Get error for this field
	const error = errors[name];
	const errorMessage = error ? (error.message as string) : "";

	// Local state for dropdown UI
	const [isOpen, setIsOpen] = useState(false);
	const [searchValue, setSearchValue] = useState("");
	const [highlightedIndex, setHighlightedIndex] = useState(-1);
	const selectRef = useRef<HTMLDivElement>(null);

	// Filter options based on search value
	const filteredOptions = searchValue
		? options.filter(option =>
			option.label.toLowerCase().includes(searchValue.toLowerCase())
		)
		: options;

	const filteredSortedOptions = filteredOptions.sort((a, b) => a.label.localeCompare(b.label));

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
				setIsOpen(false);
				setSearchValue("");
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, []);

	// Validate on mount if required and check for existing value
	useEffect(() => {
		const timeoutId = setTimeout(() => {
			if (required) {
				trigger(name);
			}
		}, 100);

		return () => clearTimeout(timeoutId);
	}, [required, name, trigger]);

	return (
		<FormGroup className={className}>
			<Label htmlFor={name} className="block text-sm font-medium text-gray-700">
				{label} {required && <span style={{ color: '#dc3545' }}>*</span>}
			</Label>

			<Controller
				name={name}
				control={control}
				render={({ field }) => {
					// Parse comma-separated string for multi-select or use single value for single-select
					const parseValue = (value: any) => {
						if (isMultiple) {
							if (!value || value === "") return [];
							return String(value).split(',').map(v => v.trim()).filter(v => v !== "");
						}
						return value;
					};

					const fieldValue = parseValue(field.value);

					// Find selected options
					const getSelectedOptions = () => {
						if (isMultiple) {
							const selectedValues = Array.isArray(fieldValue) ? fieldValue : [];
							return options.filter(option =>
								selectedValues.some(val => String(option.value) === String(val))
							);
						} else {
							if (fieldValue === undefined || fieldValue === null || fieldValue === "") {
								return null;
							}
							const selectedOption = options.find(option =>
								String(option.value) === String(fieldValue)
							);
							return selectedOption || null;
						}
					};

					// Check if field has a value
					const hasValue = isMultiple 
						? Array.isArray(fieldValue) && fieldValue.length > 0
						: fieldValue !== undefined && fieldValue !== null && fieldValue !== "";

					const selectedOptions = getSelectedOptions();

					// Determine validation state
					const isValid = hasValue && selectedOptions && !error;
					const isInvalid = hasValue && error;

					// Check if option is selected
					const isOptionSelected = (option: Option) => {
						if (isMultiple) {
							const selectedValues = Array.isArray(fieldValue) ? fieldValue : [];
							return selectedValues.some(val => String(option.value) === String(val));
						} else {
							return String(option.value) === String(fieldValue);
						}
					};

					// Get the displayed value
					const getDisplayValue = () => {
						if (isMultiple) {
							const selected = selectedOptions as Option[];
							if (!selected || selected.length === 0) {
								return <SelectPlaceholder>{placeholder}</SelectPlaceholder>;
							}

							if (selected.length <= maxDisplayTags) {
								return (
									<SelectValue>
										{selected.map((option) => (
											<SelectedTag key={option.value}>
												{option.label}
												<TagRemoveButton
													type="button"
													onClick={(e) => {
														e.stopPropagation();
														handleRemoveTag(option);
													}}
												>
													×
												</TagRemoveButton>
											</SelectedTag>
										))}
									</SelectValue>
								);
							} else {
								return (
									<SelectValue>
										{selected.slice(0, maxDisplayTags).map((option) => (
											<SelectedTag key={option.value}>
												{option.label}
												<TagRemoveButton
													type="button"
													onClick={(e) => {
														e.stopPropagation();
														handleRemoveTag(option);
													}}
												>
													×
												</TagRemoveButton>
											</SelectedTag>
										))}
										<SelectedCount>
											+{selected.length - maxDisplayTags} more
										</SelectedCount>
									</SelectValue>
								);
							}
						} else {
							const selected = selectedOptions as Option | null;
							if (!selected) {
								return <SelectPlaceholder>{placeholder}</SelectPlaceholder>;
							}
							return <SelectValue>{selected.label}</SelectValue>;
						}
					};

					// Handle removing a tag in multi-select mode
					const handleRemoveTag = (optionToRemove: Option) => {
						if (isMultiple) {
							const currentValues = Array.isArray(fieldValue) ? fieldValue : [];
							const newValues = currentValues.filter(val => 
								String(val) !== String(optionToRemove.value)
							);
							// Convert back to comma-separated string
							const newStringValue = newValues.length > 0 ? newValues.join(',') : '';
							field.onChange(newStringValue);
							trigger(name);
						}
					};

					// Handle selection of an option
					const handleSelect = (option: Option) => {
						if (isMultiple) {
							const currentValues = Array.isArray(fieldValue) ? fieldValue : [];
							const isCurrentlySelected = currentValues.some(val => 
								String(val) === String(option.value)
							);

							let newValues;
							if (isCurrentlySelected) {
								// Remove the option
								newValues = currentValues.filter(val => 
									String(val) !== String(option.value)
								);
							} else {
								// Add the option
								newValues = [...currentValues, option.value];
							}

							// Convert back to comma-separated string
							const newStringValue = newValues.length > 0 ? newValues.join(',') : '';
							field.onChange(newStringValue);
							field.onBlur(); // Mark as touched
							// Don't close dropdown in multi-select mode
							setSearchValue("");
							trigger(name);
						} else {
							field.onChange(option.value);
							field.onBlur(); // Mark as touched
							setIsOpen(false);
							setSearchValue("");
							trigger(name);
						}
					};

					// Handle clearing the selection
					const handleClear = (e: React.MouseEvent) => {
						e.stopPropagation();
						if (isMultiple) {
							field.onChange('');
						} else {
							field.onChange(null);
						}
						trigger(name);
					};

					return (
						<SelectWrapper ref={selectRef}>
							<StyledSelect
								isOpen={isOpen}
								isValid={isValid}
								isInvalid={Boolean(isInvalid)}
								onClick={() => {
									setIsOpen(!isOpen);
									if (!isOpen) {
										setSearchValue("");
										setHighlightedIndex(-1);
									}
								}}
								tabIndex={0}
								onKeyDown={(e) => {
									if (e.key === "Escape") {
										setIsOpen(false);
										setSearchValue("");
									} else if (e.key === "ArrowDown" && !isOpen) {
										setIsOpen(true);
									}
								}}
							>
								{getDisplayValue()}

								<div style={{ display: 'flex', alignItems: 'center' }}>
									{isClearable && hasValue && (
										<div
											style={{ marginRight: '8px', fontSize: '16px', cursor: 'pointer' }}
											onClick={handleClear}
										>
											×
										</div>
									)}
									<DropdownIcon isOpen={isOpen} />
								</div>
							</StyledSelect>

							<DropdownMenu isOpen={isOpen}>
								{isSearchable && (
									<SearchInput
										autoFocus
										placeholder="Search..."
										value={searchValue}
										onChange={(e) => setSearchValue(e.target.value)}
										onClick={(e) => e.stopPropagation()}
										onKeyDown={(e) => {
											if (e.key === "Escape") {
												setIsOpen(false);
												setSearchValue("");
											} else if (e.key === "ArrowDown") {
												setHighlightedIndex(prev =>
													prev < filteredSortedOptions.length - 1 ? prev + 1 : prev
												);
											} else if (e.key === "ArrowUp") {
												setHighlightedIndex(prev => prev > 0 ? prev - 1 : prev);
											} else if (e.key === "Enter" && highlightedIndex >= 0) {
												handleSelect(filteredSortedOptions[highlightedIndex]);
											}
										}}
									/>
								)}

								{filteredSortedOptions.length === 0 ? (
									<OptionItem>No options found</OptionItem>
								) : (
									filteredSortedOptions.map((option, index) => {
										const isSelected = isOptionSelected(option);

										return (
											<OptionItem
												key={option.value}
												isSelected={isSelected}
												isHighlighted={index === highlightedIndex}
												onClick={() => handleSelect(option)}
												onMouseEnter={() => setHighlightedIndex(index)}
											>
												<span>{option.label}</span>
												{isSelected && <CheckIcon />}
											</OptionItem>
										);
									})
								)}
							</DropdownMenu>

							{/* Error message */}
							{error && (
								<ErrorMessage>{errorMessage}</ErrorMessage>
							)}
						</SelectWrapper>
					);
				}}
			/>
		</FormGroup>
	);
};

export default CustomSelect;